import { useInfiniteQuery } from '@tanstack/react-query';
import axiosClient from '@/lib/auth';
import { AlertType, MonitorAlertsSearched } from '../types';
import { sortAlerts } from '../utils';
import { useAuthStore } from '@/store/authStore';

const ALERTS_QUERY_KEY = 'alerts';
const POLL_INTERVAL = 5000; // 5 seconds
const PAGE_SIZE = 10;

const getFilterParams = (
  type: AlertType,
  pageNumber: number = 1,
  pageSize: number = PAGE_SIZE,
  sortDir: 'Ascending' | 'Descending' = 'Ascending'
) => {
  const baseParams = {
    pageNumber,
    pageSize,
    sortBy: 'lastEvent',
    sortDir,
    filterOperator: 'And',
  };

  switch (type) {
    case 'new':
      return {
        ...baseParams,
        'Filter.Filters[0][action]': 'Eq',
        'Filter.Filters[0][field]': 'closedAt',
        'Filter.Filters[1][action]': 'Eq',
        'Filter.Filters[1][field]': 'actionedBy',
      };
    case 'assigned':
      return {
        ...baseParams,
        'Filter.Filters[0][action]': 'Eq',
        'Filter.Filters[0][field]': 'closedAt',
        'Filter.Filters[1][action]': 'NEq',
        'Filter.Filters[1][field]': 'actionedBy',
      };
    case 'resolved':
      return {
        ...baseParams,
        sortDir: 'Descending',
        'Filter.Filters[0][action]': 'NEq',
        'Filter.Filters[0][field]': 'closedAt',
      };
  }
};

export const useAlerts = (
  type: AlertType,
  onCountChange?: (count: number) => void,
  sortingVersion: string = 'default'
) => {
  return useInfiniteQuery({
    queryKey: [ALERTS_QUERY_KEY, type, PAGE_SIZE, sortingVersion],
    initialPageParam: 1,

    queryFn: async ({ pageParam = 1 }) => {
      const response = await axiosClient.get<MonitorAlertsSearched>('/AlertMonitoring/search', {
        params: getFilterParams(type, pageParam, PAGE_SIZE),
      });

      const alerts = response.data.rows || [];
      const sortedAlerts = sortAlerts(alerts).filter(
        (alert, index, self) => index === self.findIndex((a) => a.id === alert.id)
      );

      if (onCountChange) {
        onCountChange(response.data.totalRows || 0);
      }

      return {
        alerts: sortedAlerts.map((alert) => ({
          id: alert.id,
          alertId: alert.alertId,
          companyName: alert.companyName,
          projectName: alert.projectName,
          occurred: alert.occurred,
          status: alert.status,
          snapshot: alert.snapshot,
          actionedBy: alert.actionedBy,
          lastEvent: alert.lastEvent,
        })),
        nextPage:
          (response.data.totalRows || 0) > pageParam * PAGE_SIZE ? pageParam + 1 : undefined,
        totalRows: response.data.totalRows || 0,
      };
    },
    getNextPageParam: (lastPage) => lastPage.nextPage,
    refetchInterval: POLL_INTERVAL,
    staleTime: POLL_INTERVAL,
    notifyOnChangeProps: ['data', 'error'],
  });
};
